import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ButtonLink, SubmitButton } from "@/components/Interface/Button/Button";
import { ProfileCard } from "@/components/Layout/BaseLayouts/ProfileCard/ProfileCard";
import { expectedAuthTypeQueryParam } from "@/components/Portal/UserAccount/Security/TwoFactorAuthStatus";
import { AuthenticatorAppForm } from "@/components/Welcome/AuthenticatorSetup/AuthenticatorAppForm";
import { TwoFactorConfirmationCodeForm } from "@/components/Welcome/AuthenticatorSetup/TwoFactorConfirmationInput";
import { useAlert } from "@/hooks/useAlert";
import { useRequestTwoFactorAppSetup } from "@/hooks/useRequestTwoFactorAppSetup";
import { useValidateTwoFactorAppCode } from "@/hooks/useValidateTwoFactorAppCode";
import { portalRoutes } from "@/router/routes/portalRoutes";
import {
  useChangeTwoFactorAuthentication,
  useValidateTwoFactorAuthentication,
} from "@/services/auth";
import {
  ChangeTwoFactorAuthenticationMethod,
  ValidateTwoFactorAuthenticationMethod,
} from "@/services/model";
import { AlertTypes } from "@/utils/atoms";

export const TwoFactorAppSetup = () => {
  const showToast = useAlert();

  const navigate = useNavigate();
  const { t } = useTranslation();

  const { authSetupData, hasFailed } = useRequestTwoFactorAppSetup(
    useChangeTwoFactorAuthentication,
    {
      method: ValidateTwoFactorAuthenticationMethod.APP,
    },
    false,
  );

  useEffect(() => {
    if (hasFailed) {
      showToast({
        text: t("translation:Common.generalForm.error.genericError"),
        type: AlertTypes.error,
      });
      navigate(portalRoutes.user.security.base);
    }
  }, [hasFailed, navigate, showToast, t]);

  // Form handling
  const formMethods = useForm<TwoFactorConfirmationCodeForm>({
    defaultValues: {
      code: "",
    },
    mode: "onChange",
  });

  const { isLoading, validateTwoFactorAuth } = useValidateTwoFactorAppCode(
    () => {
      formMethods.setError("code", {
        type: "manual",
        message: t("portal:authenticator.app.setup.step2.invalidCode"),
      });
    },
    false,
    useValidateTwoFactorAuthentication,
  );

  // TODO: This should be refactored as this is completely similar to:
  // - TwoFactorSmsConfirm
  // - AuthenticatorAppSetup
  const onSubmit = (data: TwoFactorConfirmationCodeForm) => {
    validateTwoFactorAuth({
      params: {
        code: parseInt(data.code),
        method: ValidateTwoFactorAuthenticationMethod.APP,
      },
    }).then(() => {
      showToast({
        text: t("portal:user-account.security.2fa.alerts.appSetupSuccess"),
        type: AlertTypes.success,
      });
      const queryParams = new URLSearchParams();
      queryParams.set(
        expectedAuthTypeQueryParam,
        ChangeTwoFactorAuthenticationMethod.APP,
      );

      navigate(portalRoutes.user.security.base + `?${queryParams}`);
    });
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(onSubmit)}>
        <ProfileCard
          maxWidth="none"
          title={t("portal:user-account.security.2fa.initialSetup.title")}
          actions={
            <>
              <ButtonLink variant="secondary" size="sm" to={-1 as any}>
                {t("portal:user-account.security.buttons.cancel")}
              </ButtonLink>
              <SubmitButton
                fullWidth={false}
                size="sm"
                label={t("portal:authenticator.sms.verification.finishSetup")}
                disabled={isLoading}
              />
            </>
          }
        >
          <AuthenticatorAppForm authSetupData={authSetupData} />
        </ProfileCard>
      </form>
    </FormProvider>
  );
};
